import React from 'react';
import { Box } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import palette from '@ui/palette';
import Button from '@ui/Button';
import Icon from '@ui/Icon';
import { useLayoutContext } from '../../context/LayoutContext';

export const LEFT_NAVIGATION_BUTTON_OFFSET = 12;

const useStyles = makeStyles()(theme => ({
  left: {
    gridArea: 'left',
    justifyContent: 'flex-start',
  },
  right: {
    gridArea: 'right',
    justifyContent: 'flex-end',
  },
  container: {
    display: 'flex',
    alignItems: 'center',
    opacity: 1,
    visibility: 'visible',
    transition: theme.transitions.create(['opacity', 'visibility', 'width'], {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.short,
    }),
  },
  hide: {
    opacity: 0,
    visibility: 'hidden',
  },
  hideAndShrink: {
    opacity: 0,
    visibility: 'hidden',
    width: 0,
  },
  button: {
    borderRadius: theme.spacing(0.5),
    padding: theme.spacing(0.5),
    margin: theme.spacing(0, 1),
    '& > svg': {
      color: palette.gray_0,
      transition: theme.transitions.create('transform', {
        easing: theme.transitions.easing.easeOut,
        duration: theme.transitions.duration.short,
      }),
    },
  },
  mirrorIcon: {
    '& > svg': {
      transform: 'scale(-1, -1)',
    },
  },
}));

/**
 * @description A button that toggles the right rail. This button will be only hidden visibly
 * when the right rail is hidden.
 */
export const ToggleRightRailButton = () => {
  const { toggleRightRail, isRightRailHidden, isRightRailOpen } =
    useLayoutContext();
  const { classes, cx } = useStyles();

  return (
    <Box
      id="toggle-right-rail-button"
      className={cx(classes.container, classes.right, {
        [classes.hide]: isRightRailHidden,
      })}
    >
      <Button
        variant="icon"
        aria-label="right rail collapse button"
        className={cx(classes.button, {
          [classes.mirrorIcon]: isRightRailOpen,
        })}
        color="action"
        onClick={toggleRightRail}
      >
        <Icon type="alignLeft01" />
      </Button>
    </Box>
  );
};

interface ToggleLeftRailButtonProps {
  /**
   * False by default, if true the left rail button will always show unless the left rail is being hidden
   */
  alwaysShow?: boolean;
}

/**
 * @description A button that toggles the left rail. This button will be hidden and shrinks when
 * the left rail is open.
 */
export const ToggleLeftRailButton = ({
  alwaysShow,
}: ToggleLeftRailButtonProps) => {
  const { toggleLeftRail, isLeftRailOpen, isLeftRailHidden } =
    useLayoutContext();
  const { classes, cx } = useStyles();

  return (
    <Box
      className={cx(classes.container, classes.left, {
        [classes.hideAndShrink]: alwaysShow ? isLeftRailHidden : isLeftRailOpen,
      })}
      mr={0}
      // @todo Needed for transition, but is there a better way to set the val
      width={40}
    >
      <Button
        variant="icon"
        aria-label="left navigation expand button"
        className={classes.button}
        color="action"
        onClick={toggleLeftRail}
        style={{
          border: `1px solid ${palette.gray_800}`,
        }}
      >
        <Icon type="menu" color="primary" />
      </Button>
    </Box>
  );
};

export default {
  ToggleRightRailButton,
  ToggleLeftRailButton,
};
